import { BOOKING_LIFECYCLE, bookingLifecycleOf, bookingStatusOf } from './booking-status';

export type BookingStatusFilter = number | '';
export type BookingGenderFilter = 'MALE' | 'FEMALE' | 'BOTH' | '';
export const BOOKING_GENDER_FILTERS: BookingGenderFilter[] = ['MALE', 'FEMALE', 'BOTH'];

export interface BookingSearchFilters {
  familyCode: string;
  fromDate: string;
  toDate: string;
  bookingStatus: BookingStatusFilter;
  lcStatus: BookingStatusFilter; // lifecycle filter (e.g. ALLOCATED=2 for "Assigned")
  utsavId: BookingStatusFilter; // only bookings of this utsav
  guestCount: BookingStatusFilter; // exact number of guests
  gender: BookingGenderFilter; // MALE / FEMALE = only that gender among adults, BOTH = mixed
}

export type BookingSearchFilterInput = Partial<{
  familyCode: string | null;
  fromDate: string | null;
  toDate: string | null;
  bookingStatus: number | string | null;
  lcStatus: number | string | null;
  utsavId: number | string | null;
  guestCount: number | string | null;
  gender: string | null;
}>;

/**
 * Keep Family Codes as strings so their leading zeroes are never lost. The
 * booking endpoint expects an exact 12-digit value and ISO date strings.
 */
export function normalizeBookingSearchFilters(
  filters: BookingSearchFilterInput = {},
): BookingSearchFilters {
  const toStatus = (raw: number | string | null | undefined): BookingStatusFilter => {
    const n = raw === '' || raw === null || raw === undefined ? NaN : Number(raw);
    return Number.isInteger(n) && n > 0 ? n : '';
  };

  return {
    familyCode: String(filters.familyCode ?? '').replace(/\s+/g, ''),
    fromDate: String(filters.fromDate ?? '').trim(),
    toDate: String(filters.toDate ?? '').trim(),
    bookingStatus: toStatus(filters.bookingStatus),
    lcStatus: toStatus(filters.lcStatus),
    utsavId: toStatus(filters.utsavId),
    guestCount: toStatus(filters.guestCount),
    gender: toGender(filters.gender),
  };
}

function toGender(raw: string | null | undefined): BookingGenderFilter {
  const value = String(raw ?? '').trim().toUpperCase() as BookingGenderFilter;
  return BOOKING_GENDER_FILTERS.includes(value) ? value : '';
}

// Gender mix of the adults on a booking, from its male / female counts.
// Returns null when the row does not carry the counts (older backend).
export function bookingGenderOf(row: any): BookingGenderFilter | null {
  if (row?.maleCount === undefined && row?.femaleCount === undefined) return null;
  const male = Number(row?.maleCount) || 0;
  const female = Number(row?.femaleCount) || 0;
  if (male > 0 && female > 0) return 'BOTH';
  if (male > 0) return 'MALE';
  if (female > 0) return 'FEMALE';
  return '';
}

export function validateBookingSearchFilters(
  filters: BookingSearchFilters,
): string | null {
  if (filters.familyCode && !/^\d{12}$/.test(filters.familyCode)) {
    return 'Family Code must contain exactly 12 digits.';
  }

  if (filters.fromDate && filters.toDate && filters.fromDate > filters.toDate) {
    return 'From Date cannot be after To Date.';
  }

  return null;
}

/**
 * The API applies these filters, but retaining the same predicate in the UI
 * protects the table when an older backend deployment ignores one of them.
 */
export function filterBookingRows(
  rows: any[],
  filters: BookingSearchFilters,
): any[] {
  return rows.filter((row) => {
    const familyCode = String(row?.familyCode ?? '').replace(/\s+/g, '');
    const fromDate = String(row?.fromDate ?? '').slice(0, 10);
    const toDate = String(row?.toDate ?? '').slice(0, 10);
    const status = bookingStatusOf(row);
    const lifecycle = bookingLifecycleOf(row);
    // NEW also matches rows that never got a lifecycle value (not yet assigned).
    const lifecycleMatches =
      !filters.lcStatus
      || lifecycle === filters.lcStatus
      || (filters.lcStatus === BOOKING_LIFECYCLE.NEW && lifecycle === null);

    const gender = bookingGenderOf(row);

    return (!filters.familyCode || familyCode === filters.familyCode)
      && (!filters.guestCount || Number(row?.guestCount) === filters.guestCount)
      && (!filters.gender || gender === null || gender === filters.gender)
      && (!filters.fromDate || fromDate >= filters.fromDate)
      && (!filters.toDate || toDate <= filters.toDate)
      && (!filters.bookingStatus || status === filters.bookingStatus)
      && (!filters.utsavId || Number(row?.utsavId) === filters.utsavId)
      && lifecycleMatches;
  });
}
